import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Eye, FileText, TrendingUp } from "lucide-react";
import { platformClient } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CARD = "border border-white/10 bg-coal p-6";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";

const STATUS_STYLE = (s) =>
    s === "akkoord"
        ? "border-gold/60 text-gold"
        : s === "afgewezen"
          ? "border-red-400/30 text-red-300/70"
          : s === "verstuurd"
            ? "border-white/25 text-bone"
            : "border-white/15 text-ash";
const STATUS_LABELS = { concept: "Concept", verstuurd: "Verstuurd", akkoord: "Akkoord", afgewezen: "Afgewezen" };

export const AdminPipelineTab = () => {
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/billing/pipeline");
            setData(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (!data) {
        return <p data-testid="pipeline-loading" className="mt-10 text-xs uppercase tracking-[0.3em] text-ash">Pijplijn laden…</p>;
    }

    const c = data.counts;

    return (
        <div data-testid="admin-pipeline-tab" className="mt-8">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <div className={CARD} data-testid="pipe-verstuurd">
                    <p className={LABEL}><FileText className="mr-2 inline h-3.5 w-3.5 text-gold" />Verstuurd</p>
                    <p className="mt-3 font-display text-3xl text-bone">{c.verstuurd}</p>
                    <p className="mt-1 text-xs text-ash">{c.concept} in concept</p>
                </div>
                <div className={CARD} data-testid="pipe-bekeken">
                    <p className={LABEL}><Eye className="mr-2 inline h-3.5 w-3.5 text-gold" />Bekeken</p>
                    <p className="mt-3 font-display text-3xl text-bone">{c.bekeken}</p>
                    <p className="mt-1 text-xs text-ash">{data.view_rate}% van verstuurde</p>
                </div>
                <div className={CARD} data-testid="pipe-akkoord">
                    <p className={LABEL}><CheckCircle2 className="mr-2 inline h-3.5 w-3.5 text-gold" />Akkoord</p>
                    <p className="mt-3 font-display text-3xl text-gold">{c.akkoord}</p>
                    <p className="mt-1 text-xs text-ash">{data.conversion_rate}% conversie</p>
                </div>
                <div className={CARD} data-testid="pipe-mrr">
                    <p className={LABEL}><TrendingUp className="mr-2 inline h-3.5 w-3.5 text-gold" />MRR gewonnen</p>
                    <p className="mt-3 font-display text-3xl text-gold">{euro(data.mrr_won)}</p>
                    <p className="mt-1 text-xs text-ash">uit geaccepteerde offertes</p>
                </div>
                <div className={CARD} data-testid="pipe-open">
                    <p className={LABEL}>Open pijplijn</p>
                    <p className="mt-3 font-display text-3xl text-bone">{euro(data.open_value)}</p>
                    <p className="mt-1 text-xs text-ash">MRR in verstuurde offertes</p>
                </div>
            </div>

            <h3 className="mt-10 font-display text-2xl tracking-tight">Offertes ({data.quotes.length})</h3>
            <div className="mt-4 space-y-2">
                {data.quotes.map((q) => (
                    <div key={q.id} data-testid={`pipe-row-${q.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-coal px-5 py-4">
                        <div>
                            <p className="text-sm text-bone">
                                <span className="text-gold">{q.number}</span> — {q.tenant_name}
                            </p>
                            <p className="mt-1 text-xs text-ash">
                                {q.issue_date} · {euro(q.monthly_price)}/mnd + {euro(q.setup_fee)} setup
                                {q.viewed_at ? (
                                    <span className="text-bone"> · bekeken {q.viewed_at.slice(0, 10)}{q.view_count > 1 ? ` (${q.view_count}×)` : ""}</span>
                                ) : q.status !== "concept" ? (
                                    <span className="text-ash/60"> · nog niet bekeken</span>
                                ) : null}
                            </p>
                        </div>
                        <span className={`border px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE(q.status)}`}>
                            {STATUS_LABELS[q.status] || q.status}
                        </span>
                    </div>
                ))}
                {data.quotes.length === 0 ? (
                    <p data-testid="pipe-empty" className="text-sm text-ash">
                        Nog geen verkoopoffertes. Maak er een via 'Abonnementen & facturatie' → 'Maak offerte' bij een klant.
                    </p>
                ) : null}
            </div>
        </div>
    );
};
